if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.buyNowButton = this.querySelector('[data-buy-now]');
        this.buyNowButton?.addEventListener('click', this.onBuyNow.bind(this));

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      get productInfo() {
        return this.closest('product-info');
      }

      hasSizeModeOptions() {
        return Boolean(this.productInfo?.querySelector('[data-size-mode]'));
      }

      hasSizeModeSelected() {
        return Boolean(this.productInfo?.querySelector('.vocci-size-mode-btn--active'));
      }

      showSizeRequiredTip() {
        const tips = this.productInfo?.querySelectorAll('[data-size-required-tip]') || document.querySelectorAll('[data-size-required-tip]');
        let scrolled = false;
        tips.forEach((tip) => {
          tip.hidden = false;
          if (!scrolled) {
            tip.scrollIntoView({ behavior: 'smooth', block: 'center' });
            scrolled = true;
          }
        });
      }

      hideSizeRequiredTip() {
        const tips = this.productInfo?.querySelectorAll('[data-size-required-tip]') || document.querySelectorAll('[data-size-required-tip]');
        tips.forEach((tip) => {
          tip.hidden = true;
        });
      }

      // Returns false when shoppers must pick a size option first.
      ensureSizeSelected() {
        if (this.variantIdInput?.value) {
          this.hideSizeRequiredTip();
          return true;
        }

        // Color-only products have no size UI; do not block add to cart for missing size.
        if (!this.hasSizeModeOptions()) {
          return Boolean(this.variantIdInput?.value);
        }

        // Prefer the explicit size-mode check when that UI exists.
        if (!this.hasSizeModeSelected()) {
          this.showSizeRequiredTip();
          return false;
        }

        // Incomplete selection (e.g. mode chosen but no size picked yet).
        if (this.productInfo?.dataset.requireExplicitSize === 'true' || !this.variantIdInput?.value) {
          this.showSizeRequiredTip();
          return false;
        }

        return true;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        const submitButton = evt.submitter || this.submitButton;
        const loadingSpinner = submitButton.querySelector('.loading__spinner');
        if (submitButton.disabled) return;
        // Pending-size buttons stay clickable so we can show the tip.
        if (!this.ensureSizeSelected()) return;
        if (submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        submitButton.setAttribute('aria-disabled', true);
        submitButton.classList.add('loading');
        loadingSpinner?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = submitButton.querySelector('.sold-out-message');
              if (soldOutMessage) {
                submitButton.setAttribute('aria-disabled', true);
                submitButton.querySelector('span')?.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              }
              this.error = true;
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) {
              submitButton.removeAttribute('aria-disabled');
              submitButton.removeAttribute('data-pending-size');
            }
            loadingSpinner?.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      onBuyNow(evt) {
        const button = evt.currentTarget;
        if (button.disabled) return;
        // Pending-size buttons stay clickable so we can show the tip.
        if (!this.ensureSizeSelected()) return;
        if (button.getAttribute('aria-disabled') === 'true') return;

        const variantId = this.variantIdInput?.value;
        if (!variantId) return;

        const quantityInput =
          this.form.querySelector('[name="quantity"]') ||
          document.querySelector(`[name="quantity"][form="${this.form.id}"]`);
        const quantity = Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);
        const items = [`${variantId}:${quantity}`];
        const checkoutUrl = `/cart/${items.join(',')}?checkout`;

        window.location.href = checkoutUrl;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const submitButtons = this.querySelectorAll('button[type="submit"], [data-buy-now]');
        const pendingSizeOnly = Boolean(disable && !text);

        submitButtons.forEach((button) => {
          if (disable) {
            if (pendingSizeOnly) {
              // Size not chosen yet: look disabled, but remain clickable for the tip.
              button.removeAttribute('disabled');
              button.setAttribute('aria-disabled', 'true');
              button.setAttribute('data-pending-size', 'true');
            } else {
              // Sold out / unavailable: truly disabled.
              button.setAttribute('disabled', 'disabled');
              button.removeAttribute('aria-disabled');
              button.removeAttribute('data-pending-size');
            }
          } else {
            button.removeAttribute('disabled');
            button.removeAttribute('aria-disabled');
            button.removeAttribute('data-pending-size');
          }
        });

        if (disable) {
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
